import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import {
  type ICallingActionPayload,
  type IParamsCreateAICallingAgent,
  type IResponseFullAICallingAgent,
  type IResponseTemplate,
} from 'entities/AICalling/api/types'
import type { MergeField } from 'entities/MergeField/model/MergeField'
import { IntegrationKey } from 'entities/Integrations'
import {
  createActionChip,
  createHandoffToHumanAction,
  createSendSMSAction,
  type IAgentAction,
} from 'entities/AIAgent/lib/agentActions.ts'
import { type IAgentHandoffNotifySettings } from 'entities/Chatbot'
import { mergeFieldReplacer, MessageFieldStore } from 'widgets/MessageField'
import { MergeFieldsIconAction } from 'widgets/MergeField/ui/actions/MergeFieldsIconAction/MergeFieldsIconAction'

export class AgentInstructionsStore {
  private _defaultInstructions
  readonly messageFieldStore: MessageFieldStore
  readonly mergeFieldConfig = {
    isAllIntegration: true,
    includeIntegrations: [IntegrationKey.hubspot],
  }

  constructor(
    readonly entities: IEntitiesStore,
    readonly parent: {
      template?: IResponseTemplate
      response?: IResponseFullAICallingAgent | null
      params: IParamsCreateAICallingAgent | null
      actionsMap: Map<string, ICallingActionPayload>
    },
    readonly initialInstruction?: string
  ) {
    this._defaultInstructions = parent.template?.instructions || ''

    this.messageFieldStore = new MessageFieldStore(
      {
        placeholder: 'Set your agent’s role, response style, and add any specific instructions.',
        noCounter: true,
        styles: {
          minHeight: 200,
          maxHeight: 372,
        },
        disableMergeFieldsFallback: true,
        makeActions: () => {
          return [
            {
              iconButtonComponent: (
                <MergeFieldsIconAction
                  onAddMergeField={(value) => this.onChooseMergeField(value)}
                  {...this.mergeFieldConfig}
                />
              ),
            },
          ]
        },
        makeEvents: () => ({
          onFocus: this.removeError,
        }),
      },
      this.entities
    )
    this.setInstruction(
      this.initialInstruction ||
        (parent.response ? parent.response?.instructions : parent.template?.instructions) ||
        ''
    )

    makeAutoObservable(this)
  }

  init = () => {
    this.reactionTemplate()
    return () => {
      this._disposeReactionTemplate?.()
    }
  }

  private _disposeReactionTemplate: IReactionDisposer | null = null
  reactionTemplate = () => {
    this._disposeReactionTemplate?.()
    this._disposeReactionTemplate = reaction(
      () => this.parent.template,
      (template) => {
        if (!template) return
        this.setInstruction(template.instructions)
        this._defaultInstructions = template.instructions
      }
    )
  }

  private _error = ''
  get error() {
    return this._error
  }
  setError = (value: string) => {
    this._error = value
  }
  removeError = () => {
    this.setError('')
  }

  get instructions() {
    return this.messageFieldStore.text
  }
  setInstruction = (instruction: string) => {
    this.messageFieldStore.setMessageData({
      message: instruction,
      attachments: [],
      isFocus: false,
      isReplace: true,
      isReset: false,
      replaceActionChips: true,
    })
    this.removeError()
  }

  resetInstructions = () => {
    this.setInstruction(this._defaultInstructions)
  }

  checkValidate = () => {
    let validate = true
    if (!this.instructions.length) {
      validate = false
      this.setError('Add instructions for your agent')
    }
    return validate
  }
  onChooseMergeField = (field: MergeField, replaceContent?: string) => {
    if (!this.messageFieldStore.addContent) return

    const fieldContent = mergeFieldReplacer(this.entities, field.template) + '&nbsp;'

    this.messageFieldStore.addContent(fieldContent, replaceContent)
  }
  onAddAction = (action: IAgentAction) => {
    if (!this.messageFieldStore.addContent) return

    this.messageFieldStore.addContent(createActionChip(action) + '&nbsp;', '/')
  }
  replaceAction = (chip: HTMLSpanElement, action: IAgentAction) => {
    if (!this.messageFieldStore.addContent) return

    this.messageFieldStore.replaceActionChip(chip, createActionChip(action))
  }
  deleteAction = (chip: HTMLSpanElement) => {
    if (!this.messageFieldStore.addContent) return

    this.messageFieldStore.removeActionChip(chip)
  }

  onAddSMSAction = (action: ICallingActionPayload) => {
    this.parent.actionsMap.set(action.settings.token_id, action)
    this.onAddAction(createSendSMSAction({ token_id: action.settings.token_id }))
  }

  onUpdateSMSAction = (action: ICallingActionPayload) => {
    this.parent.actionsMap.set(action.settings.token_id, action)
  }

  onAddHandoffToHumanAction = (data: IAgentHandoffNotifySettings) => {
    this.onAddAction(createHandoffToHumanAction(data))
  }
  onUpdateHandoffToHumanAction = (chip: HTMLSpanElement, data: IAgentHandoffNotifySettings) => {
    this.replaceAction(chip, createHandoffToHumanAction(data))
  }

  getAgentAction = (tokenId: string) => this.parent.actionsMap.get(tokenId)
  deleteAgentAction = (tokenId: string) => this.parent.actionsMap.delete(tokenId)
}
